import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  Paper,
  IconButton,
  Box,
  Tooltip,
} from "components/mui";
import { Fullscreen, FullscreenExit } from "@mui/icons-material";
import EmailEditor from "react-email-editor";
import { Base64 } from "js-base64";
import VariableToolbar from "./VariableToolbar";
import { useNotificationProvider } from "hooks";
import { SURVEY_BUTTON_COLORS } from "constants/canmail";

// NOTE: this component assumes the viewport is wide enough for Unlayer. EmailSender
// owns that decision via DROP_EDITOR_MEDIA_QUERY and does not mount this below the
// threshold, so there is no width check here — having one in both places is what
// created the 600–999px dead zone.
function DropEditor({ onChange, onJsonChange, designToLoad, editorRef }) {
  const emailEditorRef = useRef(null);
  const [editorInstance, setEditorInstance] = useState(null);
  const [currentContent, setCurrentContent] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);

  const { notifySuccess, notifyError, notifyWarning, notifyInfo } = useNotificationProvider();

  // Show feedback to user via the app-wide notification bar.
  const showFeedback = useCallback((message, severity = "info") => {
    if (severity === "success") return notifySuccess(message);
    if (severity === "error") return notifyError(message);
    if (severity === "warning") return notifyWarning(message);
    return notifyInfo(message);
  }, [notifySuccess, notifyError, notifyWarning, notifyInfo]);

  // Handle variable insertion by copying to clipboard
  const handleVariableInsert = useCallback(async (variable) => {
    try {
      // Copy variable to clipboard
      await navigator.clipboard.writeText(variable);

      showFeedback(
        `Variable ${variable} copied to clipboard.`,
        "success"
      );
    } catch (error) {
      console.error("[DropEditor] Failed to copy variable to clipboard:", error);

      // Fallback for older browsers
      try {
        const textArea = document.createElement("textarea");
        textArea.value = variable;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);

        showFeedback(
          `Variable ${variable} copied to clipboard.`,
          "success"
        );
      } catch (fallbackError) {
        console.error("[DropEditor] Fallback clipboard copy failed:", fallbackError);
        showFeedback(
          `Failed to copy variable. Please manually insert: ${variable}`,
          "error"
        );
      }
    }
  }, [showFeedback]);

  // Toggle fullscreen mode
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Save/export design
  const onReady = (unlayer) => {
    setEditorInstance(unlayer);

    // Insert button into editor function (defined here to have access to unlayer instance)
    const insertButton = (buttonText, buttonLink) => {
      // Validate inputs
      if (!buttonText || !buttonLink) {
        console.error('[insertButton] Missing required parameters');
        showFeedback("Failed to insert button: Missing required parameters.", "error");
        return;
      }

      try {
        unlayer.exportHtml((data) => {
          try {
            // Create a deep copy of the design to avoid mutating the original
            const design = JSON.parse(JSON.stringify(data.design));
            
            // Ensure body structure exists
            if (!design.body) {
              design.body = { rows: [] };
            }
            if (!design.body.rows || !Array.isArray(design.body.rows)) {
              design.body.rows = [];
            }

            // Create button block structure matching Unlayer's format
            const buttonBlock = {
              type: "button",
              values: {
                containerPadding: "10px",
                href: {
                  name: "web",
                  values: {
                    href: buttonLink
                  }
                },
                // Literal values on purpose — this is emailed content, not app chrome.
                // See SURVEY_BUTTON_COLORS for why the theme is not used here.
                buttonColors: { ...SURVEY_BUTTON_COLORS },
                size: {
                  autoWidth: true,
                  fullWidthOnMobile: true,
                  width: "auto"
                },
                textAlign: "center",
                lineHeight: "120%",
                padding: "10px 20px",
                border: {},
                borderRadius: "4px",
                text: buttonText
              }
            };

            // Try to add button to first existing row/column if available and valid
            let buttonAdded = false;
            if (design.body.rows.length > 0) {
              const firstRow = design.body.rows[0];
              if (firstRow.columns && Array.isArray(firstRow.columns) && firstRow.columns.length > 0) {
                const firstColumn = firstRow.columns[0];
                if (!firstColumn.contents) {
                  firstColumn.contents = [];
                }
                if (Array.isArray(firstColumn.contents)) {
                  firstColumn.contents.unshift(buttonBlock);
                  buttonAdded = true;
                }
              }
            }

            // If no existing valid row/column structure, create a new minimal row
            if (!buttonAdded) {
              const newRow = {
                columns: [
                  {
                    contents: [buttonBlock]
                  }
                ]
              };
              design.body.rows.unshift(newRow);
            }

            // Reload the modified design
            unlayer.loadDesign(design);

            // Trigger export after a short delay to update state
            setTimeout(() => {
              unlayer.exportHtml((updatedData) => {
                const buttonExists = JSON.stringify(updatedData.design).includes(buttonText);
                
                if (onChange) onChange(updatedData.html);
                setCurrentContent(updatedData.html);
                if (onJsonChange) {
                  const encodedDesign = Base64.encode(JSON.stringify(updatedData.design));
                  onJsonChange(encodedDesign);
                }
                
                // Show success feedback only after verification
                if (buttonExists) {
                  showFeedback(`Survey button inserted successfully!`, "success");
                } else {
                  console.warn('[insertButton] Button not found in exported design after reload');
                  showFeedback("Button may not have been inserted correctly. Please check the editor.", "warning");
                }
              });
            }, 300);
          } catch (jsonError) {
            console.error('[insertButton] Error in JSON modification:', jsonError);
            showFeedback("Failed to insert button. Please try again.", "error");
          }
        });
      } catch (error) {
        console.error('[insertButton] Error starting button insertion:', error);
        showFeedback("Failed to insert button. Please try again.", "error");
      }
    };

    // Populate external ref if provided
    if (editorRef) {
      editorRef.current = { 
        editor: unlayer,
        insertButton: insertButton
      };
    }

    // Configure merge tags on the editor instance with custom trigger character
    // This prevents Unlayer from stripping [[variable]] brackets
    unlayer.setMergeTags({
      first_name: {
        name: "First Name",
        value: "[[first_name]]",
        sample: "John"
      },
      last_name: {
        name: "Last Name",
        value: "[[last_name]]",
        sample: "Doe"
      },
      company: {
        name: "Company",
        value: "[[company]]",
        sample: "Stream Data Systems"
      }
    });

    // Set the autocomplete trigger to recognize [ for custom [[variable]] syntax
    unlayer.setMergeTagsConfig({
      autocompleteTriggerChar: "["
    });

    unlayer.addEventListener("design:updated", () => {
      unlayer.exportHtml((data) => {
        if (onChange) onChange(data.html);
        setCurrentContent(data.html);

        if (onJsonChange) {
          const encodedDesign = Base64.encode(JSON.stringify(data.design));
          onJsonChange(encodedDesign);
        }
      });
    });
  };

  // Load design
  useEffect(() => {
    if (designToLoad && editorInstance) {
      try {
        // Set merge tags BEFORE loading design to prevent Unlayer from stripping [[variable]] brackets
        editorInstance.setMergeTags({
          first_name: {
            name: "First Name",
            value: "[[first_name]]",
            sample: "John"
          },
          last_name: {
            name: "Last Name",
            value: "[[last_name]]",
            sample: "Doe"
          },
          company: {
            name: "Company",
            value: "[[company]]",
            sample: "Stream Data Systems"
          }
        });

        editorInstance.setMergeTagsConfig({
          autocompleteTriggerChar: "["
        });

        let decoded = Base64.decode(designToLoad);
        const designJson = JSON.parse(decoded);

        editorInstance.loadDesign(designJson);

        // Trigger an export after a short delay to update state with the loaded design.
        setTimeout(() => {
          editorInstance.exportHtml((data) => {
            if (onChange) onChange(data.html);
            setCurrentContent(data.html);
            if (onJsonChange) {
              const encodedDesign = Base64.encode(JSON.stringify(data.design));
              onJsonChange(encodedDesign);
            }
          });
        }, 500);
      } catch (error) {
        console.error("[DropEditor] Failed to load design:", error);
      }
    }
  }, [designToLoad, editorInstance, onChange, onJsonChange]);

  // Fullscreen styles
  const fullscreenStyles = isFullscreen ? {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 1300,
    bgcolor: 'background.paper',
    m: 0,
    borderRadius: 0,
    p: 2,
    overflow: 'auto'
  } : {
    width: "100%",
    p: 2,
    minHeight: "200px"
  };

  return (
    <Paper sx={fullscreenStyles}>
      {/* Toolbar container with Variable Toolbar and Fullscreen toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        {/* Variable Toolbar for Drop Editor */}
        <Box sx={{ flex: 1 }}>
          <VariableToolbar
            onVariableInsert={handleVariableInsert}
            content={currentContent}
            showInsertButton={true}
            isDropEditor={true}
          />
        </Box>

        {/* Fullscreen Toggle Button */}
        <Tooltip title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
          <IconButton
            onClick={toggleFullscreen}
            color="primary"
            sx={{ ml: 1 }}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Email Editor */}
      <EmailEditor
        ref={emailEditorRef}
        onReady={onReady}
        style={{ height: isFullscreen ? 'calc(100vh - 100px)' : '800px' }}
        options={{
          mergeTags: {
            first_name: {
              name: "First Name",
              value: "[[first_name]]",
              sample: "John"
            },
            last_name: {
              name: "Last Name",
              value: "[[last_name]]",
              sample: "Doe"
            },
            company: {
              name: "Company",
              value: "[[company]]",
              sample: "Stream Data Systems"
            }
          },
          mergeTagsConfig: {
            autocompleteTriggerChar: "[",  // Tell Unlayer to recognize [ as merge tag trigger
            sort: false  // Don't sort merge tags alphabetically
          },
          smartMergeTags: false,  // CRITICAL: Disable smart merge tags to preserve raw [[variable]] syntax
          features: {
            textEditor: {
              cleanPaste: false,
              sanitizeHtml: false
            }
          },
          safeHtml: false
        }}
      />
    </Paper>
  );
}

export default DropEditor;
